import { useEffect, useState } from 'react';
import facebookAvatar from '@/assets/images/avatar-fb.png';
// Import channel images
import instagramAvatar from '@/assets/images/avatar-ig.png';
import linkedinAvatar from '@/assets/images/avatar-linkedin.png';
import useWorkflowStore from '@/stores/WorkflowStore';
import { Edit2, GitBranch, Save, Settings as SettingsIcon } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';

import { createWorkflow } from '@/hooks/workflow';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import ChannelSelectionDialog from '@/components/workflows/ChannelSelectionDialog';
import SaveWorkflow from '@/components/workflows/create/SaveWorkflow';
import WorkflowBuilder from '@/components/workflows/create/WorkflowBuilder';
import EditWorkflowDetailsDialog from '@/components/workflows/EditWorkflowDetailsDialog';
import WorkflowSettingsDialog from '@/components/workflows/WorkflowSettingsDialog';

const CreateWorkflowPage = () => {
  const [workflow, setWorkflow] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isChannelModalOpen, setIsChannelModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [tempChannels, setTempChannels] = useState([]);

  // Get workflow store data
  const {
    tempNodes,
    tempEdges,
    tempTrigger,
    workflowName,
    workflowDescription,
    channels,
    isActive,
    reEnrollment,
    enrollExisting,
    removeIfIneligible,
    setWorkflowName,
    setWorkflowDescription,
    setChannels,
    setIsActive,
    setReEnrollment,
    setEnrollExisting,
    setRemoveIfIneligible,
  } = useWorkflowStore(
    useShallow((state) => ({
      tempNodes: state.tempNodes,
      tempEdges: state.tempEdges,
      tempTrigger: state.tempTrigger,
      workflowName: state.workflowName,
      workflowDescription: state.workflowDescription,
      channels: state.channels,
      isActive: state.isActive,
      reEnrollment: state.reEnrollment,
      enrollExisting: state.enrollExisting,
      removeIfIneligible: state.removeIfIneligible,
      setWorkflowName: state.setWorkflowName,
      setWorkflowDescription: state.setWorkflowDescription,
      setChannels: state.setChannels,
      setIsActive: state.setIsActive,
      setReEnrollment: state.setReEnrollment,
      setEnrollExisting: state.setEnrollExisting,
      setRemoveIfIneligible: state.setRemoveIfIneligible,
    }))
  );

  // Channel icons mapping
  const channelIcons = {
    instagram: {
      icon: (
        <img src={instagramAvatar} alt='Instagram' width={24} height={24} />
      ),
      label: 'Instagram',
    },
    facebook: {
      icon: <img src={facebookAvatar} alt='Facebook' width={24} height={24} />,
      label: 'Facebook',
    },
    linkedin: {
      icon: <img src={linkedinAvatar} alt='LinkedIn' width={24} height={24} />,
      label: 'LinkedIn',
    },
  };

  // Generate default workflow name with timestamp
  useEffect(() => {
    const now = new Date();
    const timestamp = now.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    setWorkflowName(`Workflow ${timestamp}`);
    setWorkflowDescription('');
  }, []);

  const handleEditSave = () => {
    if (workflow) {
      setWorkflow({
        ...workflow,
        name: workflowName,
        description: workflowDescription,
      });
    }
    setIsEditing(false);
  };

  const handleChannelToggle = (channel) => {
    setTempChannels((prevChannels) =>
      prevChannels.includes(channel)
        ? prevChannels.filter((c) => c !== channel)
        : [...prevChannels, channel]
    );
  };

  const handleChannelModalOpen = () => {
    setTempChannels([...channels]);
    setIsChannelModalOpen(true);
  };

  const handleChannelModalSave = () => {
    setChannels(tempChannels);
    setIsChannelModalOpen(false);
  };

  const getChannelIcon = (channel) => {
    const channelData = channelIcons[channel];
    return channelData ? channelData.icon : null;
  };

  return (
    <div className='mx-auto h-full p-4'>
      <div className='flex flex-col gap-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <div className='rounded-md bg-blue-50 p-1.5 text-blue-600'>
              <GitBranch className='h-5 w-5' />
            </div>
            <div className='flex items-center gap-2'>
              <h1 className='max-w-[400px]  truncate text-xl font-semibold tracking-tight'>
                {workflowName}
              </h1>
              <Button
                variant='ghost'
                size='icon'
                className='h-6 w-6 p-0 hover:bg-muted'
                onClick={() => setIsEditing(true)}
              >
                <Edit2 className='h-4 w-4' />
              </Button>
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              size='sm'
              className='gap-2'
              onClick={handleChannelModalOpen}
            >
              {channels.length > 0 ? (
                <div className='flex items-center gap-1'>
                  {channels.map((channel) => (
                    <span key={channel}>{getChannelIcon(channel)}</span>
                  ))}
                </div>
              ) : (
                'Select Channels'
              )}
            </Button>

            <SaveWorkflow nodes={tempNodes} edges={tempEdges} />
          </div>
        </div>

        <Card className='h-[calc(100vh-120px)]'>
          <WorkflowBuilder />
        </Card>
      </div>

      {/* Edit Workflow Details Dialog */}
      <EditWorkflowDetailsDialog
        isOpen={isEditing}
        onOpenChange={setIsEditing}
        workflowName={workflowName}
        setWorkflowName={setWorkflowName}
        workflowDescription={workflowDescription}
        setWorkflowDescription={setWorkflowDescription}
        onSave={handleEditSave}
      />

      {/* Channel Selection Dialog */}
      <ChannelSelectionDialog
        isOpen={isChannelModalOpen}
        onOpenChange={setIsChannelModalOpen}
        tempChannels={tempChannels}
        handleChannelToggle={handleChannelToggle}
        channelIcons={channelIcons}
        onSave={handleChannelModalSave}
        trigger={tempTrigger}
        nodes={tempNodes}
      />
    </div>
  );
};

export default CreateWorkflowPage;
